(() => {
  'use strict';
  const id = value => String(value ?? '').split('/').pop();
  const enabled = value => value === true || value === 1 || value === 'true' || value === '1';
  const populated = value => Array.isArray(value) ? value.length > 0 : value && (typeof value !== 'object' || Object.keys(value).length > 0);
  function validOffer(offer, now) {
    if (offer.extension_type !== 'wholesale' || !offer.discount_id || offer.discount_method !== 'auto' || offer.type !== 'auto' || offer.discount_code) return false;
    if (enabled(offer.disabled) || offer.enabled === false || offer.active === false) return false;
    if (populated(offer.trigger_customer_tags) || populated(offer.trigger_customers) || populated(offer.trigger_data_custom) || populated(offer.conditions) || populated(offer.customer_segments) || enabled(offer.subscription_only)) return false;
    for (const [key, value] of Object.entries(offer)) {
      if (/customer|market|country|condition|selling_plan/i.test(key) && populated(value) && value !== false) return false;
    }
    for (const [key, future] of [['starts_at', true], ['ends_at', false]]) {
      if (!offer[key]) continue;
      const time = Date.parse(offer[key]);
      if (!Number.isFinite(time) || (future ? now < time : now >= time)) return false;
    }
    return Array.isArray(offer.wholesale_tier_settings) && ['percentage', 'fixed_amount'].includes(offer.discount_type);
  }
  function matches(offer, product, variant) {
    const selected = items => (items || []).some(item => id(item.id) === id(product.id) && (!item.variants || item.variants === 'ALL' || Array.isArray(item.variants) && item.variants.map(id).includes(id(variant.id))));
    if (selected(offer.trigger_data_excludes)) return false;
    if (enabled(offer.enable_trigger_data_collection_excludes) && selected(offer.trigger_data_collection_excludes)) return false;
    switch (offer.trigger_type) {
      case 'all': return true;
      case 'products': return selected(offer.trigger_data_products);
      case 'tags': return (offer.trigger_data_tags || []).some(tag => (product.tags || []).includes(tag));
      case 'collections': return (offer.trigger_data_collections_root || []).some(collection => (product.collections || []).map(id).includes(id(collection.id)));
      default: return false;
    }
  }
  function calculate(product, app, now = Date.now()) {
    if (!app || app.userStatus !== 'ACTIVATED' || !Array.isArray(app.offers) || product.requiresSellingPlan || product.nativeQuantityBreaks) return null;
    const subscription = app.subscriptions || {};
    if (subscription.subscription_status === 4 || subscription.over_capacity) return null;
    let best = null;
    const available = (product.variants || []).filter(variant => variant.available && Number.isFinite(variant.price) && variant.price > 0);
    if (!available.length) return null;
    const baseMinimum = Math.min(...available.map(variant => variant.price));
    for (const variant of available) {
      const offers = app.offers.filter(offer => validOffer(offer, now) && matches(offer, product, variant));
      if (offers.length !== 1) continue;
      const offer = offers[0];
      if (offer.discount_type === 'fixed_amount' && product.currency !== product.shopCurrency) continue;
      for (const tier of offer.wholesale_tier_settings) {
        const min = Number(tier.min_quantity);
        const max = tier.max_quantity == null || tier.max_quantity === '' || Number(tier.max_quantity) === 0 ? Infinity : Number(tier.max_quantity);
        const increment = Number(variant.increment) || 1;
        const ruleMin = Number(variant.min) || 1;
        const ruleMax = variant.max == null ? Infinity : Number(variant.max);
        if (!Number.isInteger(min) || min < 1 || !(max >= min) || !Number.isInteger(increment) || increment < 1) continue;
        const required = ruleMin + Math.ceil(Math.max(0, min - ruleMin) / increment) * increment;
        const capacity = variant.inventoryManagement && variant.inventoryPolicy !== 'continue' ? Number(variant.inventoryQuantity) : Infinity;
        if (required > Math.min(max, ruleMax, capacity)) continue;
        const rate = Number(tier.discount_value_percentage);
        const fixed = Number(tier.discount_value_fixed);
        let price;
        if (offer.discount_type === 'percentage') {
          if (!(rate > 0 && rate <= 100)) continue;
          price = Math.max(0, Math.ceil(variant.price * (100 - rate) / 100 - 1e-9));
        } else {
          if (!(fixed > 0 && Number.isFinite(fixed))) continue;
          price = Math.max(0, variant.price - Math.round(fixed * 100));
        }
        if (price >= baseMinimum) continue;
        if (!best || price < best.price || price === best.price && required < best.quantity) best = { price, quantity: required, maximum: Number.isFinite(max) ? max : null, offerId: offer.id };
      }
    }
    return best;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('dtk-card-price')) return;
  class DtkCardPrice extends HTMLElement {
    connectedCallback() { this.update(); }
    update() {
      const original = this.querySelector('[data-original-price]');
      const output = this.querySelector('[data-quantity-price]');
      if (!original || !output) return;
      try {
        const product = JSON.parse(this.dataset.product);
        const result = calculate(product, window._VL);
        if (!result) { original.hidden = false; output.hidden = true; return; }
        const money = new Intl.NumberFormat(product.locale || 'en-CA', {style: 'currency', currency: product.currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 2, maximumFractionDigits: 2}).format(result.price / 100) + ' ' + product.currency;
        output.querySelector('.dtk-quantity-price__amount').textContent = this.dataset.from.replace('__PRICE__', money);
        original.hidden = true;
        output.hidden = false;
      } catch { original.hidden = false; output.hidden = true; }
    }
  }
  customElements.define('dtk-card-price', DtkCardPrice);
  const refresh = () => document.querySelectorAll('dtk-card-price').forEach(element => element.update());
  document.addEventListener('DOMContentLoaded', refresh);
  document.addEventListener('bk-volume-mounted', refresh);
  document.addEventListener('shopify:section:load', refresh);
  window.addEventListener('pageshow', refresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  let scheduled = false;
  new MutationObserver(records => {
    if (scheduled || !records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('dtk-card-price') || node.querySelector?.('dtk-card-price'))))) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; refresh(); });
  }).observe(document.documentElement, {childList: true, subtree: true});
  setInterval(() => { if (!document.hidden) refresh(); }, 30000);
  refresh();
})();
